import styled from '@emotion/styled';
import {
  analyticsService,
  EventNames,
  ISchoolClassInfo,
  IStudentSubjectAnswerAnalysis,
} from '@taleemabad/db';
import {
  ColumnChartProps,
  ReportCardClassDashboard,
  ReportCardClassInsights,
  ReportCardResultsTab,
  Spacing,
  Tab,
  Tabs,
  Typography,
} from '@taleemabad/dsm/components';
import { convertToCamelCaseFromSpaces } from '@taleemabad/dsm/utils';
import { useReportCard } from '@taleemabad/providers';
import { FC, useState } from 'react';

const TABS = {
  RESULTS: {
    key: 1,
    label: 'Results',
  },
  INSIGHTS: {
    key: 2,
    label: 'Insights',
  },
  DASHBOARD: {
    key: 3,
    label: 'Dashboard',
  },
} as const;

const getTabs = (isInsightsEnabled: boolean): Tab[] => {
  return [
    TABS.RESULTS,
    ...(isInsightsEnabled ? [TABS.INSIGHTS] : []),
    TABS.DASHBOARD,
  ];
};

export enum FilterOptions {
  All = 'All',
  BottomThreeStudents = 'Bottom 3 Students',
  TopThreeStudents = 'Top 3 Students',
}

export const HeaderContent = () => (
  <>
    <GroupSubContainer>
      <Typography variant="textLabelLarge">Student Name</Typography>
    </GroupSubContainer>
    <Typography variant="textLabelLarge">Marks</Typography>
  </>
);

export interface StudentExamData {
  examId: string;
  name: string;
  position: number;
  percentage: number;
  obtainedMarks: number;
  totalMarks: number;
  gradeSubject?: number;
}

interface ReportCardClassResultsProps {
  classDetails: ISchoolClassInfo;
  checkedExamsData?: StudentExamData[];
  learningLevel?: ColumnChartProps;
  skillLevel?: ColumnChartProps;
  noResultsText?: string | null;
  assessmentName: string;
  emis: number;
  insights: IStudentSubjectAnswerAnalysis[];
  isInsightsEnabled?: boolean;
  onStudentClick: (examData: StudentExamData) => void;
  onInsightClick: (insight: string) => void;
}

export const ReportCardClassResultsTemplate: FC<
  ReportCardClassResultsProps
> = ({
  classDetails,
  checkedExamsData,
  learningLevel,
  skillLevel,
  noResultsText,
  assessmentName,
  insights,
  isInsightsEnabled,
  onStudentClick,
  onInsightClick,
}) => {
  const { activeTabKey, setActiveTabKey } = useReportCard();
  const [selectedFilter, setSelectedFilter] = useState<FilterOptions>(
    FilterOptions.All,
  );

  const handleTabChange = (tabKey: number) => {
    setActiveTabKey(tabKey);
    if (tabKey === TABS.INSIGHTS.key) {
      analyticsService.trackEvent(EventNames.resultsInsightsClicked, {});
    }
    if (tabKey === TABS.DASHBOARD.key) {
      analyticsService.trackEvent(EventNames.resultsDashboardClicked, {});
    }
  };

  const handleFilterChange = (filter: FilterOptions) => {
    setSelectedFilter((prevFilter) =>
      filter !== prevFilter ? filter : FilterOptions.All,
    );
  };

  const handleSelectInsight = (insight: string) => {
    const formattedInsight = convertToCamelCaseFromSpaces(
      insight.replace(' Mistakes', ''),
    );
    analyticsService.trackEvent(EventNames.resultsMistakeCategoryClicked, {
      epMistakeCategory: formattedInsight,
    });
    onInsightClick(insight);
  };

  const renderTabContent = () => {
    if (activeTabKey === TABS.RESULTS.key) {
      return (
        <ReportCardResultsTab
          classDetails={classDetails}
          checkedExamsData={checkedExamsData}
          noResultsText={noResultsText}
          assessmentName={assessmentName}
          selectedFilter={selectedFilter}
          onFilterChange={handleFilterChange}
          onStudentClick={onStudentClick}
        />
      );
    }

    if (activeTabKey === TABS.INSIGHTS.key && isInsightsEnabled) {
      return (
        <ReportCardClassInsights
          classDetails={classDetails}
          assessmentName={assessmentName}
          insights={insights ?? []}
          onSelectInsight={handleSelectInsight}
        />
      );
    }

    if (activeTabKey === TABS.DASHBOARD.key) {
      return (
        <ReportCardClassDashboard
          classDetails={classDetails}
          noResultsText={noResultsText}
          learningLevel={learningLevel}
          skillLevel={skillLevel}
          assessmentName={assessmentName}
        />
      );
    }
  };

  return (
    <>
      <Spacing marginBottom="xxl">
        <Tabs
          variant="secondary"
          activeTabKey={activeTabKey}
          tabs={getTabs(isInsightsEnabled ?? false)}
          onChange={handleTabChange}
        />
      </Spacing>
      {renderTabContent()}
    </>
  );
};

const GroupSubContainer = styled.div`
  display: flex;
  gap: var(--rk-spacing-xl);
`;
